import { useState, useEffect } from 'react';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StockTickerWidget } from '@/components/StockTickerWidget';
import { getLocalizedPath, getEquivalentPath } from '@/lib/routes';
import logo from '@/assets/logo-mufeed-nav.png';

type NavChild = { label: string; href: string; testId?: string };
type NavItem = { label: string; href?: string; children?: NavChild[] };

export const Header = ({ alwaysShowNav = false }: { alwaysShowNav?: boolean }) => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(alwaysShowNav);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const isHomePage = location.pathname === '/' || location.pathname === '/ar' || location.pathname === '/ar/';
  const isRtl = lang === 'ar';

  const navLinks: NavItem[] = [
    { label: t('header.nav.home'), href: getLocalizedPath('home', lang) },
    { label: t('header.nav.about'), href: getLocalizedPath('about', lang) },
    { label: t('header.nav.companies'), href: getLocalizedPath('companies', lang) },
    { label: t('header.nav.projects'), href: getLocalizedPath('projects', lang) },
    { label: t('header.nav.investors'), href: getLocalizedPath('investors', lang) },
    { label: t('header.nav.media'), href: getLocalizedPath('media', lang) },
    { label: t('header.nav.contact'), href: getLocalizedPath('contact', lang) },
    {
      label: t('header.nav.getInvolved'),
      children: [
        { label: t('header.nav.suppliers'), href: getLocalizedPath('suppliers', lang), testId: 'link-header-suppliers' },
        { label: t('header.nav.careers'), href: getLocalizedPath('careers', lang), testId: 'link-header-careers' },
      ],
    },
  ];

  const toggleLanguage = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    const newPath = getEquivalentPath(decodeURIComponent(location.pathname), newLang);
    navigate(newPath);
  };

  useEffect(() => {
    if (alwaysShowNav) return;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [alwaysShowNav]);

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-background/80 backdrop-blur-xl border-b border-border/50`}
      >
        <div className="container mx-auto px-6 lg:px-16 py-4">
          <div className="flex items-center justify-between">
            <Link to={getLocalizedPath('home', lang)} className="flex items-center group">
              <motion.img
                src={logo}
                alt="Mufeed"
                className="h-12 w-auto transition-all duration-300 group-hover:drop-shadow-[0_0_15px_hsl(290_50%_55%_/_0.6)]"
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 400 }}
              />
            </Link>

            <AnimatePresence>
              {isScrolled && (
                <motion.nav
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="hidden lg:flex items-center gap-8"
                >
                  {navLinks.map((link) =>
                    link.children ? (
                      <div
                        key={link.label}
                        className="relative"
                        onMouseEnter={() => setOpenDropdown(link.label)}
                        onMouseLeave={() => setOpenDropdown(null)}
                      >
                        <button
                          type="button"
                          data-testid="button-nav-get-involved"
                          className="nav-link flex items-center gap-1"
                          onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                        >
                          {link.label}
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <AnimatePresence>
                          {openDropdown === link.label && (
                            <motion.div
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.18 }}
                              className={`absolute top-full ${isRtl ? 'right-0' : 'left-0'} pt-3 min-w-[220px] z-50`}
                            >
                              <div className="bg-background/95 backdrop-blur-xl border border-border/50 rounded-lg p-2 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                                {link.children!.map((child) => (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    data-testid={child.testId}
                                    onClick={() => setOpenDropdown(null)}
                                    className="block px-3 py-2 text-xs font-bold uppercase tracking-wider text-foreground/80 hover:text-primary hover:bg-foreground/5 rounded transition-colors"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link key={link.href} to={link.href!} className="nav-link">
                        {link.label}
                      </Link>
                    )
                  )}
                </motion.nav>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-3">
              <motion.button
                data-testid="button-language-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground/80 hover:text-primary transition-colors border border-border/50 rounded-full"
                onClick={toggleLanguage}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="w-3.5 h-3.5" />
                {isRtl ? t('language.en') : t('language.ar')}
              </motion.button>

              <motion.button
                data-testid="button-mobile-menu"
                className={`p-2 text-foreground/80 hover:text-primary transition-colors ${isScrolled ? 'lg:hidden' : ''}`}
                onClick={() => setIsMobileMenuOpen(true)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Menu className="w-6 h-6" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="fixed top-[var(--header-h)] left-0 right-0 z-40">
        <StockTickerWidget />
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl overflow-y-auto"
          >
            <div className="container mx-auto px-6 py-6">
              <div className="flex items-center justify-between mb-16">
                <img src={logo} alt="Mufeed" className="h-10 w-auto" />
                <motion.button
                  className="p-2 text-foreground/80 hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </div>
              <nav className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: isRtl ? 30 : -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {link.children ? (
                      <div className="flex flex-col gap-3">
                        <span className="text-3xl font-bold text-foreground uppercase">{link.label}</span>
                        <div className={`flex flex-col gap-3 ${isRtl ? 'pr-6' : 'pl-6'} border-${isRtl ? 'r' : 'l'} border-primary/30`}>
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              data-testid={`mobile-${child.testId}`}
                              className="text-xl font-bold text-primary hover:text-primary/80 transition-colors uppercase block"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={link.href!}
                        className="text-3xl font-bold text-foreground hover:text-primary transition-colors uppercase block"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: isRtl ? 30 : -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    className="flex items-center gap-2 text-xl font-bold text-primary uppercase"
                    onClick={() => {
                      toggleLanguage();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Globe className="w-5 h-5" />
                    {isRtl ? 'English' : 'العربية'}
                  </button>
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
